// src/components/EnergyOverview.js
import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { fetchEnergySummary } from '../utils/energyApi';
import {
  formatPower, formatPowerForTable, formatTime,
  calculateDieselSavedByTime, calculateCO2Saved,
  formatDiesel, formatCO2, countUniqueSessions
} from '../utils/energyFormat';
import './HistoricData.css';
import './EnergyOverview.css';

function EnergyOverview({ authData, selectedDevice }) {
  const getDefaultDates = () => {
    const end = new Date();
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    const formatLocal = (d) => d.toISOString().split('T')[0];
    return { start: formatLocal(start), end: formatLocal(end) };
  };

  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [days, setDays] = useState([]);
  const [expandedDate, setExpandedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    if (!authData?.token || !selectedDevice?.deviceId) {
      setError('Not authenticated or device not selected.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const rows = await fetchEnergySummary(
        authData,
        selectedDevice._id || selectedDevice.deviceId,
        new Date(`${startDate}T00:00:00`),
        new Date(`${endDate}T23:59:59`)
      );
      setDays(rows);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching energy summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  const totalPowerWh = days.reduce((sum, day) => sum + day.totalPowerWh, 0);
  const totalBackupHours = days.reduce((sum, day) => sum + day.backupTimeHours, 0);
  const totalDieselSaved = calculateDieselSavedByTime(totalBackupHours);
  const totalCO2Saved = calculateCO2Saved(totalDieselSaved);

  const formatDateHeading = (date) => date.toLocaleDateString('en-US', {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
  });

  const toggleExpanded = (dateKey) => {
    setExpandedDate(prev => prev === dateKey ? null : dateKey);
  };

  const exportToExcel = () => {
    const summaryData = days.map(day => ({
      'Date': formatDateHeading(day.date),
      'Power Provided': formatPowerForTable(day.totalPowerWh),
      'Backup Time': formatTime(day.backupTimeHours),
      'Sessions': countUniqueSessions(day.sessions)
    }));
    summaryData.push({
      'Date': 'TOTAL',
      'Power Provided': formatPowerForTable(totalPowerWh),
      'Backup Time': formatTime(totalBackupHours),
      'Sessions': days.reduce((sum, day) => sum + countUniqueSessions(day.sessions), 0)
    });

    const sessionData = [];
    days.forEach(day => {
      if (!day || !Array.isArray(day.sessions)) return;
      day.sessions.forEach(session => {
        if (!session) return;
        const backupTimeSec = typeof session.backup_time === 'number' ? session.backup_time : 0;
        const powerProvidedWh = typeof session.power_provided === 'number' ? session.power_provided : 0;
        const dieselSaved = calculateDieselSavedByTime(backupTimeSec / 3600);
        sessionData.push({
          'Date': formatDateHeading(day.date || new Date()),
          'Battery': session.battery_name || 'Unknown Battery',
          'Start Time': session.start_time ? new Date(session.start_time).toLocaleString() : 'N/A',
          'End Time': session.end_time ? new Date(session.end_time).toLocaleString() : 'N/A',
          'Power Provided (kWh)': (powerProvidedWh / 1000).toFixed(2),
          'Backup Time (Hours)': (backupTimeSec / 3600).toFixed(2),
          'Diesel Saved (L)': dieselSaved.toFixed(2),
          'CO₂ Saved (kg)': calculateCO2Saved(dieselSaved).toFixed(2)
        });
      });
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryData), 'Daily Summary');
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(sessionData), 'Session Details');

    const filename = `Daily_Usage_${selectedDevice.deviceId}_${startDate}_to_${endDate}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  return (
    <div className="historic-data-view">
      <h2>Daily Usage</h2>
      <p className="subtitle">
        {selectedDevice?.vehicleId} — energy provided, backup time and estimated diesel/CO₂ savings
      </p>

      <form onSubmit={handleSubmit} className="input-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="energy-start">Start Date:</label>
            <input type="date" id="energy-start" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
          </div>
          <div className="form-group">
            <label htmlFor="energy-end">End Date:</label>
            <input type="date" id="energy-end" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
          </div>
        </div>
        <button type="submit" disabled={loading}>
          {loading ? 'Loading...' : 'Search'}
        </button>
      </form>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {days.length > 0 && (
        <div className="results">
          <div className="results-header">
            <h3>Energy Summary</h3>
            <button type="button" onClick={exportToExcel} className="export-button">Export to Excel</button>
          </div>

          <div className="summary-cards">
            <div className="summary-card">
              <h4>Total Power Provided</h4>
              <p className="summary-value">{formatPower(totalPowerWh)}</p>
            </div>
            <div className="summary-card">
              <h4>Total Backup Time</h4>
              <p className="summary-value">{formatTime(totalBackupHours)}</p>
            </div>
            <div className="summary-card environmental">
              <h4>Diesel Saved</h4>
              <p className="summary-value">{formatDiesel(totalDieselSaved)}</p>
            </div>
            <div className="summary-card environmental">
              <h4>CO₂ Emissions Saved</h4>
              <p className="summary-value">{formatCO2(totalCO2Saved)}</p>
            </div>
          </div>

          <div className="table-container">
            <table className="historic-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Date</th>
                  <th>Power Provided</th>
                  <th>Backup Time</th>
                  <th>Sessions</th>
                </tr>
              </thead>
              <tbody>
                {days.map(day => {
                  const dateKey = day.date.toISOString();
                  const hasSessions = Array.isArray(day.sessions) && day.sessions.length > 0;
                  const isExpanded = hasSessions && expandedDate === dateKey;
                  return (
                    <React.Fragment key={dateKey}>
                      <tr
                        onClick={hasSessions ? () => toggleExpanded(dateKey) : undefined}
                        style={hasSessions ? { cursor: 'pointer' } : undefined}
                      >
                        <td>{hasSessions ? (isExpanded ? '▾' : '▸') : ''}</td>
                        <td className="timestamp-cell">{formatDateHeading(day.date)}</td>
                        <td>{formatPowerForTable(day.totalPowerWh)}</td>
                        <td>{formatTime(day.backupTimeHours)}</td>
                        <td>{countUniqueSessions(day.sessions)}</td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={5}>
                            <table className="historic-table">
                              <thead>
                                <tr>
                                  <th>Battery</th>
                                  <th>Start</th>
                                  <th>End</th>
                                  <th>Power Provided</th>
                                  <th>Backup Time</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(day.sessions || []).map((session, idx) => {
                                  if (!session) return null;
                                  const backupTimeSec = typeof session.backup_time === 'number' ? session.backup_time : 0;
                                  const powerProvidedWh = typeof session.power_provided === 'number' ? session.power_provided : 0;
                                  return (
                                    <tr key={idx}>
                                      <td>{session.battery_name || 'Unknown Battery'}</td>
                                      <td>{session.start_time ? new Date(session.start_time).toLocaleString() : 'N/A'}</td>
                                      <td>{session.end_time ? new Date(session.end_time).toLocaleString() : 'N/A'}</td>
                                      <td>{formatPowerForTable(powerProvidedWh)}</td>
                                      <td>{formatTime(backupTimeSec / 3600)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && days.length === 0 && !error && (
        <div className="no-data">
          <p>Select a date range and click "Search" to load energy data.</p>
        </div>
      )}
    </div>
  );
}

export default EnergyOverview;
